import classNames from 'classnames';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import QuizHeader, { QUIZ_PROGRESS_TYPES } from './QuizHeader';
import QuizFooter from './QuizFooter';
import { QUIZ_EVENT_TYPES, QUIZ_QUESTION_TYPES } from 'src/types/Quiz';
import { QuizContext } from '../Quiz.context';
import { Loading } from '@get-mosh/msh-shared';
import styles from './quiz-question.module.scss';
import {
    QuizTextField,
    QuizNumberField,
    QuizNumberRatingField,
    QuizRatingField,
    QuizSelectField,
    QuizMultiSelectField,
    QuizPrefilled,
    QuizLoading,
    QuizOnboarding,
    QuizPhoneNumberField,
    QuizDateOfBirthField,
    QuizPhotoUpload,
    QuizLeadForm,
    QuizLeadFormSSO,
    QuizThankYouAndPassword,
    QuizPreferenceSelectField,
    QuizSuburb,
    QuizSuburbGoogle,
    QuizHowItWorks,
    QuizFirstLastName,
    QuizPasswordField,
} from 'src/components';
import { isMosh } from 'src/env.mjs';

type QuizQuestionProps = {
    auspostPostcodeSearchEnabled: boolean;
};

const QuizQuestion = ({ auspostPostcodeSearchEnabled }: QuizQuestionProps) => {
    const { currentQuestion, iterationOfProgressBar, state } =
        useContext(QuizContext);
    const { questionId } = currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [pageBottomInView, setPageBottomInView] = state.pageBottomInView;
    const [pageRendered, setPageRendered] = state.pageRendered;
    const [isLongContent] = state.isLongContent;
    const [isSmallContent] = state.isSmallContent;
    const [isOverflowing, setIsOverflowing] = state.isOverflowing;
    const [windowResized, setWindowResized] = useState(false);
    const [innerContentScrollHeight, setInnerContentScrollHeight] = useState(0);
    const [contentScrollHeight, setContentScrollHeight] = useState(0);
    const [contentScrollTop, setContentScrollTop] = useState(0);
    const [contentClientHeight, setContentClientHeight] = useState(0);

    // Content container watchers
    const contentNodeRef = useRef<HTMLDivElement>();
    const contentRef = useCallback(
        (node) => {
            if (node !== null && pageRendered) {
                contentNodeRef.current = node;
                setContentScrollHeight(node.scrollHeight);
                setContentScrollTop(node.scrollTop);
                setContentClientHeight(node.clientHeight);
            }
        },
        [pageRendered],
    );

    const innerContentRef = useCallback(
        (node) => {
            if (node !== null && pageRendered) {
                setInnerContentScrollHeight(node.scrollHeight);
            }
        },
        [pageRendered],
    );

    useEffect(() => {
        // Mark page as rendered
        setPageRendered(true);
        // Scroll to top of new page
        if (contentNodeRef?.current) {
            contentNodeRef.current.scrollTo(0, 0);
        }

        return () => {
            setPageRendered(false);
        };
    }, [contentNodeRef, questionId, setPageRendered]);

    // State for managing content sizing, overflowing and bottom reached
    useEffect(() => {
        setWindowResized(false);
        let ignore = false;
        const setPageState = () => {
            if (pageRendered && !pageTransitioning) {
                const scrollOffset = 16;
                let bottom = false;
                let isOverflowing = false;

                // Determine overflow state
                isOverflowing =
                    innerContentScrollHeight >
                    contentClientHeight - scrollOffset;
                bottom =
                    contentScrollHeight - contentScrollTop - scrollOffset <
                    contentClientHeight;
                if (!isOverflowing) bottom = true;

                // Set state if still mounted
                if (!ignore) {
                    setIsOverflowing(isOverflowing);
                    setPageBottomInView(bottom);
                }
            }
        };
        setPageState();

        return () => {
            ignore = true;
        };
    }, [
        pageTransitioning,
        windowResized,
        pageRendered,
        innerContentScrollHeight,
        contentScrollHeight,
        contentScrollTop,
        contentClientHeight,
        questionId,
        setIsOverflowing,
        setPageBottomInView,
    ]);

    // Resize listener
    useEffect(() => {
        const handleResize = () => {
            setWindowResized(true);
        };
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle scroll events
    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        setContentScrollHeight(e.currentTarget.scrollHeight);
        setContentScrollTop(e.currentTarget.scrollTop);
        setContentClientHeight(e.currentTarget.clientHeight);
    };

    // Page bottom reached event dispatch
    useEffect(() => {
        let ignore = false;
        const dispatchPageBottomInView = () => {
            if (pageBottomInView && !ignore) {
                window.dispatchEvent(new Event(QUIZ_EVENT_TYPES.scrollBottom));
            }
        };
        dispatchPageBottomInView();

        return () => {
            ignore = true;
        };
    }, [pageBottomInView, isOverflowing]);

    const darkBackgroundQuestions = [
        QUIZ_QUESTION_TYPES.loading,
        QUIZ_QUESTION_TYPES.loadingGraph,
        QUIZ_QUESTION_TYPES.loadingImage,
        QUIZ_QUESTION_TYPES.password,
        QUIZ_QUESTION_TYPES.onboarding,
    ];

    const isDarkBackground =
        darkBackgroundQuestions.includes(currentQuestion?.questionType) &&
        isMosh;

    return currentQuestion ? (
        <div
            className={classNames(
                styles['quiz-wrapper'],
                'transition-colors duration-200 ease-in-out', // smooth background transition
                {
                    'bg-green-dark': isDarkBackground,
                    'bg-white': !isDarkBackground,
                },
            )}
        >
            {/* Header */}
            <QuizHeader
                progressType={
                    iterationOfProgressBar &&
                    [
                        QUIZ_PROGRESS_TYPES.fraction,
                        QUIZ_PROGRESS_TYPES.percentage,
                    ].includes(iterationOfProgressBar)
                        ? iterationOfProgressBar
                        : undefined
                }
                variant={isDarkBackground ? 'dark' : ''}
            />
            {/* Content */}
            <div
                ref={contentRef}
                onScroll={handleScroll}
                className={classNames(
                    styles['content-wrapper'],
                    !isSmallContent ? styles['content-large'] : '',
                    isLongContent || isOverflowing
                        ? styles['content-long']
                        : '',
                    isOverflowing ? styles['content-overflowing'] : '',
                    !pageTransitioning ? styles['content--loaded'] : '',
                    isDarkBackground ? 'bg-green-dark' : '',
                )}
            >
                <div className={classNames(styles['content'], 'container')}>
                    {!currentQuestion ? (
                        <div className="container flex h-full items-center justify-center">
                            <Loading />
                        </div>
                    ) : (
                        // If no padding is set here, parent margin doesn't work for some reason
                        <div
                            data-testid="quiz-question"
                            ref={innerContentRef}
                            className="pb-[0.1px]"
                            data-question-type={currentQuestion.questionType}
                            data-question-id={currentQuestion.questionId}
                        >
                            {
                                {
                                    [QUIZ_QUESTION_TYPES.loading]: (
                                        <QuizLoading key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.onboarding]: (
                                        <QuizOnboarding key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.password]: (
                                        <QuizThankYouAndPassword
                                            key={questionId}
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.loadingGraph]: (
                                        <QuizLoading
                                            key={questionId}
                                            type="loading-graph"
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.loadingImage]: (
                                        <QuizLoading
                                            key={questionId}
                                            type="loading-image"
                                        />
                                    ),

                                    [QUIZ_QUESTION_TYPES.prefilled]: (
                                        <QuizPrefilled key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.freeText]: (
                                        <QuizTextField key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.singleChoice]: (
                                        <QuizSelectField key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.consultChoice]: (
                                        <QuizSelectField
                                            key={questionId}
                                            showWaitTime
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.multiChoice]: (
                                        <QuizMultiSelectField
                                            key={questionId}
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.iconRating]: (
                                        <QuizRatingField key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.number]: (
                                        <QuizNumberField key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.numberRating]: (
                                        <QuizNumberRatingField
                                            key={questionId}
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.leadFormSSO]: (
                                        <QuizLeadFormSSO key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.leadForm]: (
                                        <QuizLeadForm key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.phoneNumber]: (
                                        <QuizPhoneNumberField
                                            key={questionId}
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.dateOfBirth]: (
                                        <QuizDateOfBirthField
                                            key={questionId}
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.photoUpload]: (
                                        <QuizPhotoUpload key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.preferenceSelect]: (
                                        <QuizPreferenceSelectField
                                            key={questionId}
                                        />
                                    ),
                                    [QUIZ_QUESTION_TYPES.suburb]:
                                        auspostPostcodeSearchEnabled ? (
                                            <QuizSuburb key={questionId} />
                                        ) : (
                                            <QuizSuburbGoogle
                                                key={questionId}
                                            />
                                        ),
                                    [QUIZ_QUESTION_TYPES.howItWorks]: (
                                        <QuizHowItWorks key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.firstLastName]: (
                                        <QuizFirstLastName key={questionId} />
                                    ),
                                    [QUIZ_QUESTION_TYPES.passwordField]: (
                                        <QuizPasswordField key={questionId} />
                                    ),
                                }[currentQuestion?.questionType]
                            }
                        </div>
                    )}
                </div>
            </div>
            {/* Footer */}
            <QuizFooter variant={isDarkBackground ? 'dark' : 'default'} />
        </div>
    ) : null;
};

export default QuizQuestion;
